import React, { useState, useEffect, useRef } from 'react';
import Select from "react-select";
import { Loader, X } from 'lucide-react';
import Spacing from '../Spacing';
import { can_visit, notify } from "../../helpers";
import { AddInsuranceGroup, fetchGroupInsurance, UpdateInsuranceGroup } from '../../services/insurance';
import { Drawer } from 'vaul';

export default function AddAndEditInsuranceGroupModal(props) {
  const {    
    account_id,
    location_id,
    reloadData = () =>{},
    reloadDataFilter = {},
    activeModal = false,
    setActiveModal = () => {},
    editValue = {},
    editModal = false,
    previousScrollPosition = 0,
  } = props;

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    ...(editModal && { id: editValue.id }),
    name: editValue?.name ? editValue?.name : "",
    insurance_ids: editValue?.insurance_ids ? editValue?.insurance_ids : []
  }

  const [formData, setFormData] = useState(defaultValues);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [allInsurances, setAllInsurances] = useState([]);
  const dropdownRef = useRef(null);

  const getAllInsurances = async () => {
    try {
      const response = await fetchGroupInsurance(formData?.id);
      if (response?.data) {
        const allLocations = response?.data?.map((option) => ({
          value: option?.id,
          label: option?.name,
        }))
        setAllInsurances(allLocations);
      };
    } catch (error) {
      // console.log("🚀 ~ getAllLocation: ~ error:", error);
    }
  };

  useEffect(() => {
    getAllInsurances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInsuranceChange = (selectedOption) => {
    const selectedValues = selectedOption.map((option) => option.value);
    setFormData({ ...formData, insurance_ids: selectedValues });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddSubmitAction = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    try {
      if(can_visit("administrator") && can_visit("administrator_insurance_group")){
        const response = await AddInsuranceGroup(formData);
      
        if (response.detail) {
          notify('error', response.detail, 2);
        } else { 
          if(response?.data?.status === "success") {
            notify("success", response.data.message, 2);
            setFormData(defaultValues);
            reloadData(reloadDataFilter);     
            handleDrawerClose();
          } else {
            notify("error", response?.data?.message, 2);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleUpdateSubmitAction = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    try {
      if(can_visit("administrator") && can_visit("administrator_insurance_group")){
        const response = await UpdateInsuranceGroup(formData);
      
        if (response.detail) {
          notify('error', response.detail, 2);
        } else { 
          if(response?.data?.status === "success") {
            notify("success", response.data.message, 2);
            setFormData(defaultValues);
            reloadData(reloadDataFilter);     
            handleDrawerClose();
          } else {
            notify("error", response?.data?.message, 2);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleSubmitAction = editModal ? handleUpdateSubmitAction : handleAddSubmitAction;

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">{editModal ? "Edit" : "Add"} Insurance</Drawer.Title>
            <Drawer.Description className="sr-only">{editModal ? "Edit" : "Create an"} insurance group</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_auto">
              <div className="docpro_modal">
                <button
                  className="docpro_close_btn"
                  onClick={() => handleDrawerClose()}
                >
                  <X />
                </button>
                <h2 className="docpro_fs_24 docpro_mb_0">{editModal ? "Edit" : "Create an"} Group</h2>
                <Spacing lg="20" sm="20" />
                <form onSubmit={handleSubmitAction}>
                  <div className='w-full'>
                    <div>
                      <label className="docpro_fw_500 docpro_primary_color">
                        Name <span className="docpro_red_color">*</span>
                      </label>
                      <input type="text" className="docpro_form_field size_lg" name='name' required onChange={handleChange} value={formData?.name} />
                    </div>

                    <Spacing lg="20" sm="20" />
                    <div>          
                      <label className="docpro_fw_500 docpro_primary_color">
                        Select Insurance <span className="docpro_red_color">*</span>
                      </label>
                      <Select
                        required
                        isMulti
                        name="insurance_ids"
                        options={allInsurances}
                        placeholder="Search..."
                        isSearchable
                        value={allInsurances?.filter((option) => 
                          formData.insurance_ids?.includes(option.value)
                        ) || []}
                        onChange={handleInsuranceChange}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        filterOption={(option, rawInput) => {
                          const words = rawInput.toLowerCase().split(" ");
                          return words.every((word) =>
                            option.label.toLowerCase().includes(word)
                          );
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderColor: 'var(--border)',
                            borderRadius: '8px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: '-2px',
                            borderRadius: '0 0 3px 3px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                            padding: 0
                          }),
                        }}
                      />
                    </div>
                  </div>

                  <div className='text-right'>
                    <Spacing lg="35" sm="35" />
                    <div className='flex items-center docpro_grid_gap_10' style={{ justifyContent: "end" }}>
                      <button
                        type='button'
                        className="docpro_bordered_btn docpro_fw_600 size_lg"
                        onClick={() => handleDrawerClose()}
                      >
                        Cancel
                      </button>
                      <button
                        type='submit'
                        className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                      >
                        {editModal ?
                          <span>Update</span>
                        :
                          <span>Save</span>
                        }
                        {submitLoader && (
                          <span className="docpro_loading">
                            <Loader width={20} height={20} />
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
